import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { _axios } from "../utils/axios";
import { TApplication, TPopulatedForm } from "./applications.hooks";

export type TReviewerEditChange = {
  label: string;
  oldValue: string;
  newValue: string;
};

export type TReviewerEditState = {
  application: TApplication & Record<string, unknown>;
  /** Every field the reviewer may edit, as one form. */
  form: TPopulatedForm;
  canEdit: boolean;
  lockReason: string | null;
};

export const reviewerEditKeyStore = {
  state: (id: number | string) => ["reviewer-edit-application", id],
};

export function useReviewerEditState({ id }: { id: number }) {
  return useQuery({
    queryKey: reviewerEditKeyStore.state(id),
    queryFn: () =>
      _axios.get<{ success: true; data: TReviewerEditState }>(
        `/applications/reviewer-edit/${id}`
      ),
    select: (d) => d?.data?.data,
    enabled: !!id,
    /** Always read fresh, so the form never starts from values edited elsewhere. */
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSaveReviewerEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      remark,
    }: {
      id: number;
      data: Record<string, unknown>;
      remark: string;
    }) =>
      _axios.put<{
        success: boolean;
        data: {
          application: TApplication & Record<string, unknown>;
          changes: TReviewerEditChange[];
        };
      }>(`/applications/reviewer-edit/${id}`, { data, remark }),
    retry: false,
    /**
     * The edited values are shown on the reviewer's details, Demand Note and
     * Audit History screens, each cached under its own key, so every cached
     * query is refreshed after a save.
     */
    onSuccess: () => qc.invalidateQueries(),
  });
}
